import { faChevronUp, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import '../styles/componentStyles/NumberInput.css';

interface NumberInputProps {
  type?: 'inline' | 'normal';
  startValue?: number;
  minValue?: number;
  maxValue?: number;
  step?: number;
  cycle?: boolean;
  width?: string;
  suffix?: string;
  inputName: string;
  controlsInfo?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  ariaLabel?: string;
  onIncrease?: () => void;
  onDecrease?: () => void;
  onSetValue?: (value: number) => void;
}

function NumberInput(props: NumberInputProps): JSX.Element {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState<number>(props.startValue || 0);
  const { required, disabled, readOnly } = props;

  useEffect(() => {
    setInputValue(props.startValue || 0);
  }, [props.startValue]);

  const minValue = props.minValue !== undefined ? props.minValue : 0;
  const maxValue = props.maxValue !== undefined ? props.maxValue : 100;
  const step = props.step || 1;

  function saveValue(value: number): void {
    if (props.onSetValue) {
      props.onSetValue(Number(value));
    }
  }

  function increaseValue(): void {
    if (props.onIncrease) {
      props.onIncrease();
      return;
    }

    let newValue = Number(inputValue);
    if (newValue < maxValue) {
      newValue = Number(newValue) + Number(step);
      if (newValue > maxValue) {
        newValue = maxValue;
      }
    } else if (props.cycle) {
      newValue = minValue;
    } else {
      newValue = maxValue;
    }
    setInputValue(newValue);
    saveValue(newValue);
  }

  function decreaseValue(): void {
    if (props.onDecrease) {
      props.onDecrease();
      return;
    }

    let newValue = Number(inputValue);
    if (newValue > minValue) {
      newValue = Number(newValue) - Number(step);
      if (newValue < minValue) {
        newValue = minValue;
      }
    } else if (props.cycle) {
      newValue = maxValue;
    } else {
      newValue = minValue;
    }
    setInputValue(newValue);
    saveValue(newValue);
  }

  function inputChangeHandler(event: React.ChangeEvent<HTMLInputElement>): void {
    setInputValue(Number(event.target.value));
    saveValue(Number(event.target.value));
  }

  function getAriaLabel(): string {
    if (props.ariaLabel) {
      return props.ariaLabel;
    }
    if (disabled || readOnly) {
      return `${t('trackers.numberInput.inactive')} ${props.controlsInfo || ''}`;
    }
    return `${t('trackers.numberInput.change')} ${props.controlsInfo || ''}`;
  }

  return (
    <div className={`number-input-wrapper${props.width || ''}`}>
      <div className={`number-input ${props.type || ''}`}>
        <input
          type="number"
          className="number-value fs-exclude"
          id={`${props.inputName}-input`}
          name="number-input"
          value={inputValue}
          onChange={inputChangeHandler}
          max={maxValue}
          min={minValue}
          step={step}
          aria-label={getAriaLabel()}
          required={required}
          readOnly={readOnly}
          disabled={disabled}
        />
        {props.suffix && (
          <label htmlFor={`${props.inputName}-input`} className="input-suffix">
            {props.suffix}
          </label>
        )}
        {!disabled && !readOnly && (
          <div className="number-controls">
            <button
              className="up-btn"
              onClick={increaseValue}
              type="button"
              aria-label={`${t('A134')} ${props.controlsInfo || ''}`}
            >
              <FontAwesomeIcon icon={faChevronUp} className="icon" aria-hidden />
            </button>
            <button
              className="down-btn"
              onClick={decreaseValue}
              type="button"
              aria-label={`${t('A135')} ${props.controlsInfo || ''}`}
            >
              <FontAwesomeIcon icon={faChevronDown} className="icon" aria-hidden />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

NumberInput.defaultProps = {
  startValue: 0,
  minValue: 0,
  maxValue: 100,
  step: 1,
  cycle: false,
  width: '',
  suffix: '',
  controlsInfo: '',
  disabled: false,
  readOnly: false,
  ariaLabel: undefined,
  onIncrease: undefined,
  onDecrease: undefined,
  onSetValue: undefined,
};

export default NumberInput;
